import {useDisclosure} from '@chakra-ui/react'
import {useEffect, useMemo, useState} from 'react'
import {useFormContext} from 'react-hook-form'
import {isEmpty} from 'lodash-es'
import {Maybe} from 'graphql/jsutils/Maybe'
import {useEngravingTabs} from 'modules/product/hooks/useEngravingTabs'
import {
    EngraveOptionsKeys,
    EngraveTab,
    PendantSide,
    SelectedEngraveOption,
    SelectedEngraveOptions,
} from '_app/types/product'
import {engraveOptionName} from 'modules/product/utils/engraveOptionName'
import {BundleItemOption} from '_app/types/api'
import {getEngravingOptions} from 'modules/product/utils/getEngravingOptions'
import {CartEngravingSkeletonProps} from '../components/CartEngravingSkeleton'
import {EngravePendantsOptions, ExtendedCustomizableEngraveValue,} from '../components/engraving/types'
import toast from '../../../_app/utils/toast'
import {useTranslation} from 'next-i18next'
import {GTM_EVENTS} from '../../../_app/utils/dataLayer'
import {useRouter} from '../../../_app/hooks/useRouter'

const getUniqueElements = (options: CartEngravingSkeletonProps['options']) => {
    const optionsSet = (options || [])
        .map(item => ({
            sku: item.value?.pendant,
            pendantIdx: item.value?.pendant_idx,
        }))
        .filter(
            (value): value is { sku: string; pendantIdx: number } =>
                !!value.sku && !!value.pendantIdx
        )

    const seenItems = new Set<string>()
    const uniqueSku = optionsSet.filter(option => {
        const id = `${option.sku}-${option.pendantIdx}`
        const duplicate = seenItems.has(id)
        seenItems.add(id)

        return !duplicate
    })

    return uniqueSku
}

type Props = Pick<
    CartEngravingSkeletonProps,
    'options' | 'productPrice' | 'productItems'
> & {
    isConfiguratorPage?: boolean;
};

export const useEngraving = ({
    options,
    productPrice,
    productItems,
    isConfiguratorPage,
  }: Props) => {
    const {isOpen, onOpen, onClose} = useDisclosure()
    const { locale } = useRouter()
    const [isCloseModalOpen, setIsCloseModalOpen] = useState(false)
    const [pendingOption, setPendingOption] = useState<{
        name: string;
        data: any;
    } | null>(null)
    const { t }: any = useTranslation()
    const engravingTabsObj = useEngravingTabs()
    const {getValues, setValue, watch, formState} = useFormContext()
    const {isSubmitting, isSubmitted} = formState
    const onDrawerClose = () => {
        const selectedOptions = getValues('selected_engrave_options')

        if (!isEmpty(selectedOptions)) {
            setIsCloseModalOpen(true)
        } else {
            handleDrawerClose()
        }
    }

    const handleDrawerClose = () => {
        if (isConfiguratorPage) {
            return
        }

        setValue('selected_engrave_options', undefined)
        setValue('engrave_options', undefined)
        setActivePendantId(pendantsKeys[0])
        setActiveSide(
            pendantsOptions[activePendantId]?.front?.length ? 'front' : 'back'
        )
        setActiveTabId(pendantSideTabs[0]?.id)

        onClose()
        setIsCloseModalOpen(false)
    }

    const getBundleElement = (sku: string) => {
        let output

        productItems?.find(item => {
            const foundElement = item?.options?.find(
                option => option?.product?.sku === sku
            )

            if (foundElement) {
                output = foundElement

                return true
            }
        })

        return output as unknown as Maybe<BundleItemOption>
    }

    useEffect(() => {
        if (isSubmitted && !isSubmitting) {
            handleDrawerClose()
        }
    }, [isSubmitting, isSubmitted])

    const engravingTabs = Object.values(engravingTabsObj)

    const pendantsOptions = useMemo<EngravePendantsOptions>(() => {
        const tempOptions: EngravePendantsOptions = {}

        const uniqueSku = getUniqueElements(options)

        uniqueSku.forEach(({sku, pendantIdx}, index) => {
            options?.forEach(option => {
                const value = option.value
                const element = getBundleElement(sku)

                if (!tempOptions[index + 1] && value?.pendant_idx === pendantIdx) {
                    tempOptions[index + 1] = {
                        front: [] as ExtendedCustomizableEngraveValue[],
                        back: [] as ExtendedCustomizableEngraveValue[],
                        name: element?.product?.name || '',
                        pendantIdx: value.pendant_idx,
                    }
                }
                if (
                    value?.placement === 'front' &&
                    value.pendant === sku &&
                    value.pendant_idx === pendantIdx
                ) {
                    tempOptions[index + 1]?.front?.push({
                        ...value,
                        title: option.title,
                        img: element?.product?.front,
                    } as unknown as ExtendedCustomizableEngraveValue)
                }
                if (
                    value?.placement === 'back' &&
                    value.pendant === sku &&
                    value.pendant_idx === pendantIdx
                ) {
                    tempOptions[index + 1]?.back?.push({
                        ...value,
                        title: option.title,
                        img: element?.product?.back,
                    } as unknown as ExtendedCustomizableEngraveValue)
                }
            })
        })

        return tempOptions
    }, [options])

    const getPreviewImage = () => {
        return pendantsOptions?.[activePendantId]?.[activeSide]?.find(
            item => item?.code === activeTabId
        )?.img
    }
    const pendantsKeys = Object.keys(pendantsOptions)
    const [activePendantId, setActivePendantId] = useState(pendantsKeys[0])
    const [activePendantSku, setActivePendantSku] = useState('')
    const [activeSide, setActiveSide] = useState<PendantSide>(
        pendantsOptions[activePendantId]?.front?.length ? 'front' : 'back'
    )
    const [activePendantIdx, setActivePendantIdx] = useState(
        options?.[0]?.value?.pendant_idx || 1
    )
    const pendantSideTabs: EngraveTab[] = useMemo(
        () =>
            (pendantsOptions[activePendantId]?.[activeSide] || [])
                .map(({code}) => engravingTabsObj[code as EngraveOptionsKeys])
                .sort((a, b) => a.order - b.order),
        [pendantsOptions, activePendantId, activeSide]
    )
    const [activeTabId, setActiveTabId] = useState<string>(pendantSideTabs[0]?.id)
    const activeTab = engravingTabs?.find(({id}) => id === activeTabId)
    const ActiveTabComponent = activeTab?.component
    useEffect(() => {
        if (isOpen && pendantsKeys?.length > 0) {
            if (activePendantIdx) {
                setActivePendantId(pendantsKeys[activePendantIdx - 1])
            } else {
                setActivePendantId(pendantsKeys[0])
            }
        }
    }, [isOpen, pendantsKeys])
    useEffect(() => {
        if (
            !activeSide ||
            !pendantsOptions[activePendantId]?.[activeSide]?.length
        ) {
            setActiveSide(pendantsOptions[activePendantId]?.front?.length ? 'front' : 'back')
        }
        setActivePendantSku(
            pendantsOptions[activePendantId]?.back?.[0]?.pendant ||
            pendantsOptions[activePendantId]?.front?.[0]?.pendant ||
            ''
        )
    }, [activePendantId, pendantsOptions, activeSide])
    useEffect(() => {
        if (pendantSideTabs.some(side => side.id === activeTabId)) return
        setActiveTabId(pendantSideTabs[0]?.id)
    }, [activePendantId, activeSide, activeTabId, pendantSideTabs])
    const activeOption = options?.find(
        o =>
            o.value?.pendant === activePendantSku &&
            o.value.placement === activeSide &&
            o.value.pendant_idx === activePendantIdx &&
            o.value.code === activeTab?.id
    )
    useEffect(() => {
        setValue(`${engraveOptionName(activeOption)}.value`, undefined)
    }, [activePendantId, activeSide])
    const onAddOption = () => {
        if (activeOption) {
            const optionValues = getValues(engraveOptionName(activeOption))
            if (optionValues?.value || optionValues?.file) {
                const selectedOptionName = `selected_engrave_options.${activeOption.value?.pendant}.${activeOption.value?.placement}.${activeOption.value?.pendant_idx}`
                const prevValue = getValues(selectedOptionName)
                const data = {
                    title: activeOption.title,
                    ...activeOption.value,
                    ...optionValues,
                }
                if (prevValue) setPendingOption({name: selectedOptionName, data})
                else setValue(selectedOptionName, data)
            }
        }
    }
    useEffect(() => {
        if (pendingOption) {
            setValue(pendingOption.name, pendingOption.data)
        }
    }, [pendingOption])
    const onRemoveOption = (
        option: Pick<SelectedEngraveOption, 'pendant' | 'placement' | 'pendant_idx'>
    ) => {
        setValue(
            `selected_engrave_options.${option.pendant}.${option.placement}.${option.pendant_idx}`,
            undefined
        )
    }
    const selectedEngraveOptions = (watch('selected_engrave_options') ||
        {}) as SelectedEngraveOptions
    const selectedEngraveOptionsArray = getEngravingOptions(selectedEngraveOptions)
    const totalPrice =
        (productPrice.value || 0) +
        selectedEngraveOptionsArray.reduce(
            (acc, cur) => (cur?.price ? acc + cur.price : acc),
            0
        )
    const isFree = activeOption?.value?.price === 0
    useEffect(() => {
        if (options) {
            const groupedByPendant = options.reduce((acc, item) => {
                const idx: any = item?.value?.pendant
                if (!acc[idx]) {
                    acc[idx] = []
                }
                acc[idx].push(item)
                return acc
            }, {})
            const mergedOptions = Object.values(groupedByPendant).map((group: any) => {
                return {
                    pendant: group?.[0].value.pendant,
                    pendant_idx: group?.[0].value.pendant_idx,
                    details: group?.map((item: any) => ({
                        placement: item.value.placement,
                        max_characters: item.value.max_characters
                    }))
                }
            })
            const pendantIdxList = mergedOptions.map((item: any) => item?.pendant_idx)
            const hasDuplicates = new Set(pendantIdxList).size !== pendantIdxList.length
            if (hasDuplicates) {
                GTM_EVENTS.errors({
                    message: t('PROBLEM_WITH_CONFIGURE'),
                    path: window?.location?.href,
                    locale,
                })
                toast({ description: 'Jeśli wybrałeś zawieszkę typu Puzderko, dodaj informację o treści grawerunku w komentarzu do zamówienia. Dzięki temu spersonalizujemy ją specjalnie dla Ciebie!', status: 'warning' })
            }
        }
    }, [options])
    return {
        buttonProps: activeTab?.buttonProps || {},
        onOpen,
        onDrawerClose,
        isOpen,
        isCloseModalOpen,
        setIsCloseModalOpen,
        handleDrawerClose,
        pendantsOptions,
        activePendantId,
        setActivePendantId,
        activeSide,
        setActiveSide,
        pendantSideTabs,
        activeTabId,
        getPreviewImage,
        isFree,
        ActiveTabComponent,
        activeOption,
        isSubmitting,
        totalPrice,
        onAddOption,
        onRemoveOption,
        selectedEngraveOptionsArray,
        setActiveTabId,
        activePendantIdx,
        setActivePendantIdx,
    }
}
